import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ro } from "date-fns/locale";
import {
  Play,
  Clock,
  Plus,
  Timer,
  Trash2,
  Edit,
  X,
  Filter,
  TrendingUp,
  ShieldX,
  Download,
  FileSpreadsheet,
  FileText,
  Users,
  Briefcase,
  User,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DateAutoInput } from "../components/DateAutoInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { CaseSelect } from "../components/CaseSelect";
import { ClientSelect } from "../components/ClientSelect";
import { UserSelect } from "../components/UserSelect";
import { API } from "../config/api";

export default function BillingPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("time");
  const [loading, setLoading] = useState(true);
  
  // Check if user is colaborator_extern - they shouldn't access this page
  const isColaboratorExtern = user?.position === "colaborator_extern";
  const isAdmin = user?.role === "admin";
  
  // Time tracking state
  const [timeEntries, setTimeEntries] = useState([]);
  const [allTimeEntries, setAllTimeEntries] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerElapsed, setTimerElapsed] = useState(0);
  const [showTimeEntryDialog, setShowTimeEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Reports state
  const [reportData, setReportData] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  
  // Filter state
  const [dateFilter, setDateFilter] = useState({
    start: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd")
  });
  
  // Report filters
  const [reportFilter, setReportFilter] = useState({
    type: "all", // all, client, case, user
    clientName: "",
    caseId: "",
    userId: ""
  });
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'single' | 'filtered', id?: string }
  
  // Data for dropdowns
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Activity types
  const ACTIVITY_TYPES = [
    { value: "consultatie", label: "Consultație" },
    { value: "redactare", label: "Redactare" },
    { value: "cercetare", label: "Cercetare" },
    { value: "instanta", label: "Instanță" },
    { value: "sedinta", label: "Ședință" },
    { value: "corespondenta", label: "Corespondență" },
    { value: "deplasare", label: "Deplasare" },
    { value: "altele", label: "Altele" },
  ];

  // Activity type helper functions
  const getActivityLabel = (type) => {
    const found = ACTIVITY_TYPES.find(t => t.value === type);
    return found ? found.label : type || "";
  };
  
  const getActivityColor = (type) => {
    const colors = {
      consultatie: "bg-blue-100 text-blue-700",
      redactare: "bg-purple-100 text-purple-700",
      cercetare: "bg-indigo-100 text-indigo-700",
      instanta: "bg-red-100 text-red-700",
      sedinta: "bg-orange-100 text-orange-700",
      corespondenta: "bg-teal-100 text-teal-700",
      deplasare: "bg-amber-100 text-amber-700",
      altele: "bg-gray-100 text-gray-600",
    };
    return colors[type] || "bg-gray-100 text-gray-600";
  };

  // Form state for new time entry
  const [timeEntryForm, setTimeEntryForm] = useState({
    case_id: "",
    client_id: "",
    client_name: "",
    activity_type: "consultatie",
    description: "",
    date: format(new Date(), "yyyy-MM-dd"),
    duration_minutes: 60,
    hourly_rate: 150,
    billable: true,
    notes: ""
  });
  
  // Form state for starting timer
  const [timerForm, setTimerForm] = useState({
    case_id: "",
    client_id: "",
    client_name: "",
    activity_type: "consultatie",
    description: "Lucru la dosar"
  });

  // Redirect colaborator_extern
  useEffect(() => {
    if (isColaboratorExtern) {
      toast.error("Nu aveți acces la această secțiune");
      navigate("/dashboard");
    }
  }, [isColaboratorExtern, navigate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (isColaboratorExtern) return;
    
    setLoading(true);
    try {
      const [entriesRes, timerRes, casesRes, clientsRes, allEntriesRes] = await Promise.all([
        axios.get(`${API}/billing/time-entries?start_date=${dateFilter.start}&end_date=${dateFilter.end}`, getAuthHeader()),
        axios.get(`${API}/billing/timer/active`, getAuthHeader()),
        axios.get(`${API}/cases?limit=500`, getAuthHeader()),
        axios.get(`${API}/clients?limit=500`, getAuthHeader()),
        axios.get(`${API}/billing/time-entries`, getAuthHeader()),
      ]);
      
      setTimeEntries(entriesRes.data || []);
      setAllTimeEntries(allEntriesRes.data || []);
      setActiveTimer(timerRes.data);
      setCases(casesRes.data || []);
      setClients(clientsRes.data || []);
      
      if (isAdmin) {
        try {
          const [summaryRes, usersRes] = await Promise.all([
            axios.get(`${API}/billing/reports/summary?start_date=${dateFilter.start}&end_date=${dateFilter.end}`, getAuthHeader()),
            axios.get(`${API}/users/team`, getAuthHeader())
          ]);
          setReportSummary(summaryRes.data);
          
          // Add admin (current user) to the users list
          const teamMembers = usersRes.data || [];
          const adminUser = {
            id: user?.id,
            name: user?.name || "Admin",
            email: user?.email,
            role: "admin",
            position: "admin"
          };
          
          // Check if admin is already in the list
          const adminExists = teamMembers.some(u => u.id === user?.id);
          if (!adminExists) {
            setUsers([adminUser, ...teamMembers]);
          } else {
            setUsers(teamMembers);
          }
        } catch (e) {
          console.error("Error fetching admin data:", e);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, dateFilter, isAdmin, isColaboratorExtern]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Timer interval
  useEffect(() => {
    let interval;
    if (activeTimer) {
      const startTime = new Date(activeTimer.started_at).getTime();
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setTimerElapsed(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  // Format duration
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTimerDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Timer actions
  const startTimer = async () => {
    try {
      const params = new URLSearchParams();
      if (timerForm.case_id && timerForm.case_id !== "none") {
        params.append("case_id", timerForm.case_id);
      }
      if (timerForm.client_id) {
        params.append("client_id", timerForm.client_id);
      }
      if (timerForm.client_name) {
        params.append("client_name", timerForm.client_name);
      }
      if (timerForm.activity_type) {
        params.append("activity_type", timerForm.activity_type);
      }
      params.append("description", timerForm.description);
      
      const res = await axios.post(
        `${API}/billing/timer/start?${params.toString()}`,
        {},
        getAuthHeader()
      );
      setActiveTimer(res.data);
      setTimerElapsed(0);
      toast.success("Timer pornit");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la pornirea timer-ului");
    }
  };

  const stopTimer = async (billable = true) => {
    try {
      const params = new URLSearchParams();
      params.append("billable", billable);
      
      await axios.post(
        `${API}/billing/timer/stop?${params.toString()}`,
        {},
        getAuthHeader()
      );
      setActiveTimer(null);
      setTimerElapsed(0);
      toast.success("Timer oprit și înregistrare salvată");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la oprirea timer-ului");
    }
  };

  const cancelTimer = async () => {
    try {
      await axios.delete(`${API}/billing/timer/cancel`, getAuthHeader());
      setActiveTimer(null);
      setTimerElapsed(0);
      toast.success("Timer anulat");
    } catch (error) {
      toast.error("Eroare la anularea timer-ului");
    }
  };

  // Time entry actions
  const saveTimeEntry = async () => {
    try {
      const formData = { ...timeEntryForm };
      if (formData.case_id === "none") formData.case_id = "";
      
      if (editingEntry) {
        await axios.put(
          `${API}/billing/time-entries/${editingEntry.id}`,
          formData,
          getAuthHeader()
        );
        toast.success("Înregistrare actualizată");
      } else {
        await axios.post(`${API}/billing/time-entries`, formData, getAuthHeader());
        toast.success("Înregistrare adăugată");
      }
      setShowTimeEntryDialog(false);
      setEditingEntry(null);
      resetTimeEntryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la salvare");
    }
  };

  const deleteTimeEntry = async (id) => {
    try {
      await axios.delete(`${API}/billing/time-entries/${id}`, getAuthHeader());
      toast.success("Înregistrare ștearsă");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const resetTimeEntryForm = () => {
    setTimeEntryForm({
      case_id: "",
      client_id: "",
      client_name: "",
      activity_type: "consultatie",
      description: "",
      date: format(new Date(), "yyyy-MM-dd"),
      duration_minutes: 60,
      hourly_rate: 150,
      billable: true,
      notes: ""
    });
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setTimeEntryForm({
      case_id: entry.case_id || "",
      client_id: entry.client_id || "",
      client_name: entry.client_name || "",
      activity_type: entry.activity_type || "altele",
      description: entry.description,
      date: entry.date,
      duration_minutes: entry.duration_minutes,
      hourly_rate: entry.hourly_rate,
      billable: entry.billable,
      notes: entry.notes || ""
    });
    setShowTimeEntryDialog(true);
  };

  // Filter entries for reports
  // Unique client names from ALL time entries (for report filter, not date-filtered)
  const reportClientNames = useMemo(() => {
    const names = new Set();
    // Use allTimeEntries if available, otherwise timeEntries
    const source = allTimeEntries || timeEntries;
    source.forEach(e => {
      if (e.client_name) names.add(e.client_name);
    });
    return [...names].sort();
  }, [allTimeEntries, timeEntries]);

  const filteredReportEntries = useMemo(() => {
    // When filtering by client, use ALL entries (not date-filtered) so user sees all history
    let source = (reportFilter.type === "client" && reportFilter.clientName) ? allTimeEntries : timeEntries;
    let filtered = [...source];
    
    if (reportFilter.type === "client" && reportFilter.clientName) {
      filtered = filtered.filter(e => e.client_name === reportFilter.clientName);
    } else if (reportFilter.type === "case" && reportFilter.caseId) {
      filtered = filtered.filter(e => e.case_id === reportFilter.caseId);
    } else if (reportFilter.type === "user" && reportFilter.userId) {
      filtered = filtered.filter(e => e.user_id === reportFilter.userId);
    }
    
    return filtered;
  }, [timeEntries, allTimeEntries, reportFilter]);

  // Calculate totals for filtered entries (TVA = 19%)
  const TVA_RATE = 0.19;
  const filteredTotals = useMemo(() => {
    const totalMinutes = filteredReportEntries.reduce((sum, e) => sum + e.duration_minutes, 0);
    const totalAmount = filteredReportEntries.reduce((sum, e) => sum + (e.amount || 0), 0);
    const totalWithTVA = totalAmount * (1 + TVA_RATE);
    return {
      hours: `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`,
      amount: totalAmount,
      tva: totalAmount * TVA_RATE,
      amountWithTVA: totalWithTVA
    };
  }, [filteredReportEntries]);

  // Export functions
  const exportToCSV = () => {
    const headers = ["Data", "Descriere", "Dosar", "Client", "Avocat", "Durată (min)", "Tarif/h", "Valoare"];
    const rows = filteredReportEntries.map(e => [
      e.date,
      e.description,
      e.case_number_intern || e.case_number || "-",
      e.client_name || "-",
      e.user_name || "-",
      e.duration_minutes,
      e.hourly_rate,
      e.amount || 0
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(r => r.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `raport_timp_${dateFilter.start}_${dateFilter.end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Raport exportat ca CSV");
  };

  const exportToExcel = async () => {
    try {
      // Create a simple HTML table that Excel can open
      const headers = ["Data", "Descriere", "Dosar", "Client", "Avocat", "Durată", "Tarif/h", "Valoare"];
      const rows = filteredReportEntries.map(e => [
        format(parseISO(e.date), "dd.MM.yyyy"),
        e.description,
        e.case_number_intern || e.case_number || "-",
        e.client_name || "-",
        e.user_name || "-",
        formatDuration(e.duration_minutes),
        `${e.hourly_rate} RON`,
        `${e.amount || 0} RON`
      ]);
      
      let html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="UTF-8"></head>
        <body>
        <table border="1">
          <tr style="background:#f0f0f0;font-weight:bold">${headers.map(h => `<td>${h}</td>`).join("")}</tr>
          ${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("")}
          <tr style="font-weight:bold">
            <td colspan="5">TOTAL</td>
            <td>${filteredTotals.hours}</td>
            <td></td>
            <td>${filteredTotals.amount.toLocaleString()} RON</td>
          </tr>
        </table>
        </body></html>
      `;
      
      const blob = new Blob([html], { type: "application/vnd.ms-excel" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `raport_timp_${dateFilter.start}_${dateFilter.end}.xls`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Raport exportat ca Excel");
    } catch (error) {
      toast.error("Eroare la export");
    }
  };

  // Delete filtered entries
  const handleDeleteFiltered = async () => {
    if (deleteTarget?.type === "single" && deleteTarget.id) {
      await deleteTimeEntry(deleteTarget.id);
    } else if (deleteTarget?.type === "filtered") {
      try {
        // Delete all filtered entries
        for (const entry of filteredReportEntries) {
          await axios.delete(`${API}/billing/time-entries/${entry.id}`, getAuthHeader());
        }
        toast.success(`${filteredReportEntries.length} înregistrări șterse`);
        fetchData();
      } catch (error) {
        toast.error("Eroare la ștergerea înregistrărilor");
      }
    }
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  // Get filter label
  const getFilterLabel = () => {
    if (reportFilter.type === "client" && reportFilter.clientName) {
      return reportFilter.clientName;
    } else if (reportFilter.type === "case" && reportFilter.caseId) {
      const caseItem = cases.find(c => c.id === reportFilter.caseId);
      return caseItem?.numar_dosar_intern || caseItem?.numar_dosar || "Dosar selectat";
    } else if (reportFilter.type === "user" && reportFilter.userId) {
      const userItem = users.find(u => u.id === reportFilter.userId);
      return userItem?.name || "Avocat selectat";
    }
    return "Toate înregistrările";
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalMinutes = timeEntries.reduce((sum, e) => sum + e.duration_minutes, 0);
    const totalAmount = timeEntries.reduce((sum, e) => sum + (e.amount || 0), 0);
    return {
      totalHours: `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`,
      totalAmount
    };
  }, [timeEntries]);

  if (loading && timeEntries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="billing-page">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Evidență Timp</h1>
          <p className="text-muted-foreground">
            Înregistrează și monitorizează timpul de lucru
          </p>
        </div>
      </div>
      </div>{/* End Sticky */}

      {/* Active Timer Display */}
      {activeTimer && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-3xl font-mono font-bold text-green-700">
                  {formatTimerDisplay(timerElapsed)}
                </div>
                <div>
                  <div className="font-medium">{activeTimer.description}</div>
                  {activeTimer.case_number && (
                    <div className="text-sm text-muted-foreground">
                      Dosar: {activeTimer.case_number}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => stopTimer(true)} className="bg-green-600 hover:bg-green-700">
                  Salvează
                </Button>
                <Button variant="outline" onClick={cancelTimer}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ore Lucrate</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalHours}</div>
            <p className="text-xs text-muted-foreground">
              {dateFilter.start} - {dateFilter.end}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valoare Totală</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalAmount.toLocaleString()} RON</div>
            <p className="text-xs text-muted-foreground">
              {timeEntries.length} înregistrări
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Filtru Dată</CardTitle>
            <Filter className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <DateAutoInput
                value={dateFilter.start}
                onChange={(val) => setDateFilter(prev => ({ ...prev, start: val }))}
                className="text-sm"
              />
              <DateAutoInput
                value={dateFilter.end}
                onChange={(val) => setDateFilter(prev => ({ ...prev, end: val }))}
                className="text-sm"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      {isAdmin ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Timer className="w-4 h-4" />
              Evidență Timp
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Rapoarte
            </TabsTrigger>
          </TabsList>

          {/* Time Tracking Tab */}
          <TabsContent value="time" className="space-y-4">
            {renderTimerSection()}
            {renderTimeEntriesTable(true)}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            {/* Report Filters */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtrare Rapoarte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-4">
                  <div>
                    <Label>Tip Filtru</Label>
                    <Select
                      value={reportFilter.type}
                      onValueChange={(v) => setReportFilter({ type: v, clientName: "", caseId: "", userId: "" })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toate înregistrările</SelectItem>
                        <SelectItem value="client">Per Client</SelectItem>
                        <SelectItem value="case">Per Dosar</SelectItem>
                        <SelectItem value="user">Per Avocat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {reportFilter.type === "client" && (
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={reportFilter.clientName || "all"}
                        onValueChange={(v) => setReportFilter(prev => ({ ...prev, clientName: v === "all" ? "" : v }))}
                      >
                        <SelectTrigger data-testid="report-client-select">
                          <SelectValue placeholder="Selectează client..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toți clienții</SelectItem>
                          {reportClientNames.map(name => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {reportFilter.type === "case" && (
                    <div>
                      <Label>Dosar</Label>
                      <CaseSelect
                        cases={cases}
                        clients={clients}
                        value={reportFilter.caseId || "none"}
                        onValueChange={(v) => setReportFilter(prev => ({ ...prev, caseId: v }))}
                        placeholder="Selectează dosar..."
                        emptyText="Toate dosarele"
                        allowNone={true}
                        data-testid="report-case-select"
                      />
                    </div>
                  )}

                  {reportFilter.type === "user" && (
                    <div>
                      <Label>Avocat</Label>
                      <UserSelect
                        users={users}
                        value={reportFilter.userId || "none"}
                        onValueChange={(v) => setReportFilter(prev => ({ ...prev, userId: v }))}
                        placeholder="Selectează avocat..."
                        emptyText="Toți avocații"
                        allowNone={true}
                        data-testid="report-user-select"
                      />
                    </div>
                  )}

                  <div className="flex items-end gap-2">
                    <Button variant="outline" onClick={exportToCSV} className="flex-1">
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="outline" onClick={exportToExcel} className="flex-1">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                    <Button variant="outline" onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (reportFilter.type === "client" && reportFilter.clientName) {
                          params.set("client_name", reportFilter.clientName);
                        }
                        if (reportFilter.type === "case" && reportFilter.caseId) {
                          params.set("case_id", reportFilter.caseId);
                        }
                        if (reportFilter.type === "user" && reportFilter.userId) {
                          params.set("user_id", reportFilter.userId);
                        }
                        const response = await axios.get(
                          `${API}/export/time-tracking-report?${params.toString()}`,
                          { ...getAuthHeader(), responseType: 'blob' }
                        );
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `raport_time_tracking_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success("PDF exportat");
                      } catch (error) {
                        toast.error("Eroare la export PDF");
                      }
                    }} className="flex-1" data-testid="export-pdf-time-tracking">
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Summary */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{getFilterLabel()}</CardTitle>
                    <CardDescription>
                      {filteredReportEntries.length} înregistrări • {filteredTotals.hours} • {filteredTotals.amount.toLocaleString()} RON (fără TVA) • {filteredTotals.amountWithTVA.toLocaleString(undefined, {maximumFractionDigits: 2})} RON (cu TVA)
                    </CardDescription>
                  </div>
                  {filteredReportEntries.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setDeleteTarget({ type: "filtered" });
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Șterge toate ({filteredReportEntries.length})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Data</th>
                        <th className="text-left p-3 text-sm font-medium">Descriere</th>
                        <th className="text-left p-3 text-sm font-medium">Dosar</th>
                        <th className="text-left p-3 text-sm font-medium">Client</th>
                        <th className="text-left p-3 text-sm font-medium">Avocat</th>
                        <th className="text-right p-3 text-sm font-medium">Durată</th>
                        <th className="text-right p-3 text-sm font-medium">Valoare</th>
                        <th className="text-center p-3 text-sm font-medium">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReportEntries.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center p-8 text-muted-foreground">
                            Nu există înregistrări pentru filtrele selectate
                          </td>
                        </tr>
                      ) : (
                        filteredReportEntries.map(entry => (
                          <tr key={entry.id} className="border-t hover:bg-muted/30">
                            <td className="p-3 text-sm">
                              {format(parseISO(entry.date), "dd MMM yyyy", { locale: ro })}
                            </td>
                            <td className="p-3 text-sm">{entry.description}</td>
                            <td className="p-3 text-sm">
                              {entry.case_number_intern || entry.case_number || "-"}
                            </td>
                            <td className="p-3 text-sm">{entry.client_name || "-"}</td>
                            <td className="p-3 text-sm">{entry.user_name || "-"}</td>
                            <td className="p-3 text-sm text-right font-mono">
                              {formatDuration(entry.duration_minutes)}
                            </td>
                            <td className="p-3 text-sm text-right font-semibold">
                              {(entry.amount || 0).toLocaleString()} RON
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setDeleteTarget({ type: "single", id: entry.id });
                                  setShowDeleteConfirm(true);
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {filteredReportEntries.length > 0 && (
                      <tfoot className="bg-muted/50 font-semibold text-sm">
                        <tr>
                          <td colSpan={5} className="p-3 text-right">TOTAL (fără TVA):</td>
                          <td className="p-3 text-right font-mono">{filteredTotals.hours}</td>
                          <td className="p-3 text-right">{filteredTotals.amount.toLocaleString(undefined, {maximumFractionDigits: 2})} RON</td>
                          <td></td>
                        </tr>
                        <tr className="border-t">
                          <td colSpan={5} className="p-3 text-right text-muted-foreground">TVA (19%):</td>
                          <td></td>
                          <td className="p-3 text-right text-muted-foreground">{filteredTotals.tva.toLocaleString(undefined, {maximumFractionDigits: 2})} RON</td>
                          <td></td>
                        </tr>
                        <tr className="border-t-2 border-primary/30">
                          <td colSpan={5} className="p-3 text-right font-bold">TOTAL (cu TVA):</td>
                          <td></td>
                          <td className="p-3 text-right font-bold">{filteredTotals.amountWithTVA.toLocaleString(undefined, {maximumFractionDigits: 2})} RON</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* User Stats */}
            {reportSummary?.by_user?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Productivitate per Avocat
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Avocat</th>
                        <th className="text-right p-3 text-sm font-medium">Ore Totale</th>
                        <th className="text-right p-3 text-sm font-medium">Ore Facturabile</th>
                        <th className="text-right p-3 text-sm font-medium">Valoare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportSummary.by_user.map((stat, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-3 font-medium">{stat.user_name}</td>
                          <td className="p-3 text-right">{stat.hours}h</td>
                          <td className="p-3 text-right">{stat.billable_hours}h</td>
                          <td className="p-3 text-right font-semibold">{stat.amount.toLocaleString()} RON</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        // Team Member View - Only Time Tracking, simplified
        <div className="space-y-4">
          {renderTimerSection()}
          {renderTimeEntriesTable(false)}
        </div>
      )}

      {/* Time Entry Dialog */}
      <Dialog open={showTimeEntryDialog} onOpenChange={setShowTimeEntryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Editează Înregistrare" : "Adaugă Înregistrare Timp"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client</Label>
              <ClientSelect
                clients={clients}
                value={timeEntryForm.client_id || "none"}
                onValueChange={(value, customName) => {
                  if (value === "__custom__") {
                    setTimeEntryForm(prev => ({ ...prev, client_id: "", client_name: customName || "" }));
                  } else {
                    const selected = clients.find(c => c.id === value);
                    setTimeEntryForm(prev => ({
                      ...prev,
                      client_id: value === "none" ? "" : value,
                      client_name: selected ? (selected.nume || selected.denumire || "") : "",
                    }));
                  }
                }}
                placeholder="Selectează client"
                emptyText="Fără client"
                allowNone={true}
                allowCustom={true}
                customDisplayName={!timeEntryForm.client_id && timeEntryForm.client_name ? timeEntryForm.client_name : ""}
                data-testid="time-entry-client-select"
              />
            </div>
            <div>
              <Label>Tip Activitate</Label>
              <Select
                value={timeEntryForm.activity_type}
                onValueChange={(v) => setTimeEntryForm(prev => ({ ...prev, activity_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descriere *</Label>
              <Input
                value={timeEntryForm.description}
                onChange={(e) => setTimeEntryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ce ai lucrat?"
              />
            </div>
            <div>
              <Label>Dosar (opțional)</Label>
              <CaseSelect
                cases={cases}
                clients={clients}
                value={timeEntryForm.case_id || "none"}
                onValueChange={(v) => setTimeEntryForm(prev => ({ ...prev, case_id: v }))}
                placeholder="Selectează dosar..."
                emptyText="Fără dosar"
                allowNone={true}
                data-testid="time-entry-case-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data</Label>
                <DateAutoInput
                  value={timeEntryForm.date}
                  onChange={(val) => setTimeEntryForm(prev => ({ ...prev, date: val }))}
                />
              </div>
              <div>
                <Label>Durată (minute)</Label>
                <Input
                  type="number"
                  value={timeEntryForm.duration_minutes}
                  onChange={(e) => setTimeEntryForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            {/* Duration presets */}
            <div className="flex flex-wrap gap-1.5">
              {[15, 30, 45, 60, 90, 120, 180, 240].map(mins => (
                <Button
                  key={mins}
                  type="button"
                  size="sm"
                  variant={timeEntryForm.duration_minutes === mins ? "default" : "outline"}
                  className={`h-7 px-2 text-xs ${timeEntryForm.duration_minutes === mins ? "bg-teal-600 hover:bg-teal-700" : ""}`}
                  onClick={() => setTimeEntryForm(prev => ({ ...prev, duration_minutes: mins }))}
                >
                  {mins < 60 ? `${mins}min` : `${mins / 60}h`}
                </Button>
              ))}
            </div>
            {/* Admin-only fields */}
            {isAdmin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tarif orar (RON)</Label>
                    <Input
                      type="number"
                      value={timeEntryForm.hourly_rate}
                      onChange={(e) => setTimeEntryForm(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id="billable"
                      checked={timeEntryForm.billable}
                      onCheckedChange={(checked) => setTimeEntryForm(prev => ({ ...prev, billable: checked }))}
                    />
                    <Label htmlFor="billable" className="cursor-pointer">Facturabil</Label>
                  </div>
                </div>
              </>
            )}
            <div>
              <Label>Note (opțional)</Label>
              <Textarea
                value={timeEntryForm.notes}
                onChange={(e) => setTimeEntryForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeEntryDialog(false)}>
              Anulează
            </Button>
            <Button onClick={saveTimeEntry} disabled={!timeEntryForm.description}>
              {editingEntry ? "Salvează" : "Adaugă"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmare ștergere</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "filtered" 
                ? `Sigur doriți să ștergeți toate cele ${filteredReportEntries.length} înregistrări filtrate? Această acțiune nu poate fi anulată.`
                : "Sigur doriți să ștergeți această înregistrare? Această acțiune nu poate fi anulată."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFiltered} className="bg-red-600 hover:bg-red-700">
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // Helper function to render timer section
  function renderTimerSection() {
    if (activeTimer) return null;
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Timer className="w-5 h-5" />
            Pornește Timer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Client</Label>
              <ClientSelect
                clients={clients}
                value={timerForm.client_id || "none"}
                onValueChange={(value, customName) => {
                  if (value === "__custom__") {
                    setTimerForm(prev => ({ ...prev, client_id: "", client_name: customName || "" }));
                  } else {
                    const selected = clients.find(c => c.id === value);
                    setTimerForm(prev => ({
                      ...prev,
                      client_id: value === "none" ? "" : value,
                      client_name: selected ? (selected.nume || selected.denumire || "") : "",
                    }));
                  }
                }}
                placeholder="Selectează client"
                emptyText="Fără client"
                allowNone={true}
                allowCustom={true}
                customDisplayName={!timerForm.client_id && timerForm.client_name ? timerForm.client_name : ""}
                data-testid="timer-client-select"
              />
            </div>
            <div>
              <Label>Tip Activitate</Label>
              <Select
                value={timerForm.activity_type}
                onValueChange={(v) => setTimerForm(prev => ({ ...prev, activity_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dosar (opțional)</Label>
              <CaseSelect
                cases={cases}
                clients={clients}
                value={timerForm.case_id || "none"}
                onValueChange={(v) => setTimerForm(prev => ({ ...prev, case_id: v }))}
                placeholder="Selectează dosar..."
                emptyText="Fără dosar"
                allowNone={true}
                data-testid="timer-case-select"
              />
            </div>
            <div>
              <Label>Descriere</Label>
              <Input
                value={timerForm.description}
                onChange={(e) => setTimerForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ce lucrezi?"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={startTimer} className="bg-green-600 hover:bg-green-700">
              <Play className="w-4 h-4 mr-2" />
              Start Timer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Helper function to render time entries table
  function renderTimeEntriesTable(showAllColumns) {
    return (
      <>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Înregistrări Timp</h3>
          <Button onClick={() => { resetTimeEntryForm(); setEditingEntry(null); setShowTimeEntryDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Adaugă Manual
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium">Data</th>
                    <th className="text-left p-3 text-sm font-medium">Client</th>
                    <th className="text-left p-3 text-sm font-medium">Descriere</th>
                    <th className="text-left p-3 text-sm font-medium">Activitate</th>
                    <th className="text-left p-3 text-sm font-medium">Dosar</th>
                    <th className="text-right p-3 text-sm font-medium">Durată</th>
                    {showAllColumns && (
                      <>
                        <th className="text-right p-3 text-sm font-medium">Tarif/h</th>
                        <th className="text-right p-3 text-sm font-medium">Valoare (fără TVA)</th>
                        <th className="text-center p-3 text-sm font-medium">Acțiuni</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.length === 0 ? (
                    <tr>
                      <td colSpan={showAllColumns ? 9 : 6} className="text-center p-8 text-muted-foreground">
                        Nu există înregistrări pentru perioada selectată
                      </td>
                    </tr>
                  ) : (
                    timeEntries.map(entry => (
                      <tr key={entry.id} className="border-t hover:bg-muted/30">
                        <td className="p-3 text-sm">
                          {format(parseISO(entry.date), "dd MMM yyyy", { locale: ro })}
                        </td>
                        <td className="p-3 text-sm font-medium">
                          {entry.client_name || "-"}
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-sm">{entry.description}</div>
                          {showAllColumns && entry.user_name && (
                            <div className="text-xs text-muted-foreground">{entry.user_name}</div>
                          )}
                        </td>
                        <td className="p-3">
                          {entry.activity_type && (
                            <Badge className={`text-[10px] px-1.5 py-0.5 ${getActivityColor(entry.activity_type)}`}>
                              {getActivityLabel(entry.activity_type)}
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          {entry.case_number_intern || entry.case_number || "-"}
                        </td>
                        <td className="p-3 text-sm text-right font-mono">
                          {formatDuration(entry.duration_minutes)}
                        </td>
                        {showAllColumns && (
                          <>
                            <td className="p-3 text-sm text-right">
                              {entry.hourly_rate} RON
                            </td>
                            <td className="p-3 text-sm text-right font-semibold">
                              {(entry.amount || 0).toLocaleString()} RON
                            </td>
                            <td className="p-3">
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(entry)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setDeleteTarget({ type: "single", id: entry.id });
                                    setShowDeleteConfirm(true);
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }
}
